import { useCallback, useEffect, useState } from 'react';
import { SignaturesAffaire } from './signatures-affaire';
import { api, ErreurApi, type DossierLigne, type FicheAffaire } from './api';
import { ParcoursAffaireVue } from './parcours-affaire';
// §1 · §1 bis · §2 — ce que l'acte imprimera « À COMPLÉTER », et de quoi le combler.
import { FormulaireDeLActe } from './formulaire-acte';
import { FormulaireDepot } from './mutations';
import { CorpsParcelle } from './parcelle';
import { acteAttendu } from '../../src/partage/acte-attendu.js';

/**
 * LES DOSSIERS (§31) — la file du guichet, et le suivi d'une demande.
 *
 * ⚠️ CET ÉCRAN RÉPOND À « OÙ EN EST MON DOSSIER », et c'est la question que
 * l'administration ne pouvait pas traiter. Avant lui, une demande n'existait
 * qu'à partir du dépôt d'une mutation — donc après le levé, donc des semaines
 * après la visite de l'usager, qui n'avait ni numéro ni preuve de dépôt.
 *
 * ⚠️ LE FIL DES ÉTAPES EST EN AJOUT SEUL, ET C'EST VISIBLE. Une étape ne se
 * corrige pas en réécrivant le passé : elle se corrige en inscrivant la
 * suivante. La base le refuse (`trg_etape_ajout_seul`), l'écran ne propose donc
 * aucune modification d'étape — proposer un geste que la base refuse est pire
 * que ne pas le proposer.
 */

const COULEUR_ETAPE: Record<string, string> = {
  ouvert: 'bleu', leve_demande: 'ambre', leve_recu: 'ambre',
  parcelle_etablie: 'bleu', acte_depose: 'bleu', publicite_ouverte: 'ambre',
  instruction: 'ambre', titre_delivre: 'vert', remis: 'vert', clos: 'gris',
};

const LIBELLE_ETAPE: Record<string, string> = {
  ouvert: 'Ouvert au guichet',
  leve_demande: 'Levé demandé',
  leve_recu: 'Levé reçu',
  parcelle_etablie: 'Parcelle établie',
  acte_depose: 'Acte déposé',
  publicite_ouverte: 'Publicité ouverte',
  instruction: 'En instruction',
  titre_delivre: 'Titre délivré',
  remis: 'Remis au requérant',
  clos: 'Clos',
};
const libelleEtape = (e: string) => LIBELLE_ETAPE[e] ?? e.replace(/_/g, ' ');

export function Dossiers({
  peutOuvrir, peutClore, peutDeposerActe, onOuvrirParcelle, onOuvrirMutation,
  referenceInitiale, permissionsBien,
}: {
  /** fon.mutation:creer — sans elle le service refuserait le dépôt de l'acte. */
  peutDeposerActe: boolean;
  peutOuvrir: boolean; peutClore: boolean;
  onOuvrirParcelle: (id: string) => void;
  onOuvrirMutation: (reference: string) => void;
  /**
   * Permissions du BIEN, transmises telles quelles au corps de l'affaire.
   * Distinctes de celles de la procédure : `fon.affaire` et `cad.parcelle` sont
   * deux ressources, et le service les tranche séparément.
   */
  permissionsBien: {
    peutActiver: boolean; peutRattacherFiscal: boolean;
    peutValider: boolean; peutTraiterAnomalie: boolean;
  };
  /**
   * Dossier à ouvrir d'emblée, désigné depuis AILLEURS que cette liste.
   *
   * ⚠️⚠️ SANS CE PROP, LE DOSSIER ÉTAIT INATTEIGNABLE DEPUIS LA PARCELLE.
   * `setOuvert` était un état PRIVÉ de cet écran : rien, dans toute
   * l'application, ne pouvait demander « ouvre GN-DOS-000000005 ». Le panneau
   * de parcelle NOMMAIT pourtant le dossier dans son bloc « Procédures », et
   * `procedures.tsx` porte depuis toujours un bouton « Ouvrir le dossier »
   * conditionné à un rappel `onOuvrirDossier` que PERSONNE ne lui passait
   * (0 occurrence dans `parcelle.tsx`, mesuré le 14/09/2026).
   *
   * L'agent voyait donc, sur la même affaire, le numéro du dossier écrit en
   * toutes lettres — et aucun chemin pour y aller. Rien n'échouait : il n'y
   * avait simplement pas de porte, et une porte absente ne rougit pas.
   */
  referenceInitiale?: string | null;
}) {
  const [liste, setListe] = useState<DossierLigne[]>([]);
  const [etapes, setEtapes] = useState<string[]>([]);
  const [filtre, setFiltre] = useState('');
  const [vivants, setVivants] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [ouvert, setOuvert] = useState<string | null>(referenceInitiale ?? null);

  /**
   * ⚠️ ON SUIT LA DEMANDE EXTERNE, sans écraser une navigation en cours : si
   * l'agent a ouvert un autre dossier entre-temps, c'est la DERNIÈRE demande
   * venue de l'extérieur qui gagne — celle sur laquelle il vient de cliquer.
   */
  useEffect(() => {
    if (referenceInitiale) setOuvert(referenceInitiale);
  }, [referenceInitiale]);

  const charger = useCallback(async () => {
    setErreur(null);
    try {
      const r = await api.affaires.liste({ etape: filtre || undefined, vivants });
      setListe(r.affaires ?? []);
    } catch (e) {
      setErreur(e instanceof ErreurApi && e.estRefus
        ? "Votre rôle ne permet pas de consulter les dossiers (fon.affaire:lire)."
        : String((e as Error).message ?? e));
    }
  }, [filtre, vivants]);

  useEffect(() => { void charger(); }, [charger]);
  useEffect(() => { api.affaires.etapes().then((r) => setEtapes(r.etapes)).catch(() => setEtapes([])); }, []);

  if (ouvert) {
    return (
      <FicheDossierVue
        reference={ouvert}
        peutClore={peutClore}
        onFerme={() => { setOuvert(null); void charger(); }}
        peutDeposerActe={peutDeposerActe}
        onOuvrirParcelle={onOuvrirParcelle}
        onOuvrirMutation={onOuvrirMutation}
        permissionsBien={permissionsBien}
      />
    );
  }

  return (
    <div className="tableau-bord">
      <header className="entete-scene">
        <h2>Dossiers fonciers</h2>
        <div className="actions">
          {/* ⚠️ ON FILTRE PAR ÉTAPE, PAS PAR « en cours ». Un dossier bloqué à
              « levé demandé » depuis trois semaines et un dossier en
              instruction depuis hier sont tous deux « en cours » — et ce n'est
              pas la même situation pour le guichet. */}
          <select value={filtre} onChange={(e) => setFiltre(e.target.value)} aria-label="Filtrer par étape">
            <option value="">Toutes les étapes</option>
            {etapes.map((e) => <option key={e} value={e}>{libelleEtape(e)}</option>)}
          </select>
          <label className="bascule">
            <input type="checkbox" checked={vivants} onChange={(e) => setVivants(e.target.checked)} />
            Vivants seulement
          </label>
          <button className="bouton primaire" disabled={!peutOuvrir}
                  title={peutOuvrir ? "Ouvrir un dossier au guichet" : "Votre rôle ne permet pas d'ouvrir un dossier (fon.affaire:creer)"}
                  onClick={() => setOuvert('__nouveau__')}>
            Ouvrir un dossier
          </button>
        </div>
      </header>

      <div className="avertissement">
        <strong>Un dossier par parcelle.</strong> Deux dossiers vivants sur le même fonds
        seraient deux procédures concurrentes — la base les refuse. Pour reprendre une
        parcelle déjà engagée, poursuivez le dossier existant ou closez-le.
      </div>

      {erreur && <p className="erreur" role="alert">{erreur}</p>}

      {liste.length === 0 ? (
        <p className="vide">
          Aucun dossier{filtre ? ` à l'étape « ${libelleEtape(filtre)} »` : vivants ? ' vivant' : ''}.
        </p>
      ) : (
        <table className="tableau large">
          <thead>
            <tr><th>Dossier</th><th>Requérant</th><th>Parcelle</th><th>Étape</th><th>Actes</th><th>Ouvert le</th></tr>
          </thead>
          <tbody>
            {liste.map((d) => (
              <tr key={d.reference}>
                <td>
                  <button className="lien" onClick={() => setOuvert(d.reference)}>
                    <code>{d.reference}</code>
                  </button>
                </td>
                <td>{d.requerant}</td>
                <td>{d.parcelle
                  ? <button className="lien" onClick={() => onOuvrirParcelle(d.parcelle!)}><code>{d.parcelle}</code></button>
                  : <span className="discret">pas encore établie</span>}</td>
                <td><span className={`etat ${COULEUR_ETAPE[d.etape_courante] ?? 'gris'}`}>{libelleEtape(d.etape_courante)}</span></td>
                <td>{d.actes}</td>
                <td>{new Date(d.ouvert_le).toLocaleDateString('fr-FR')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function FicheDossierVue({
  reference, peutClore, peutDeposerActe, onFerme, onOuvrirParcelle, onOuvrirMutation,
  permissionsBien,
}: {
  peutDeposerActe: boolean;
  reference: string; peutClore: boolean; onFerme: () => void;
  onOuvrirParcelle: (id: string) => void; onOuvrirMutation: (r: string) => void;
  /**
   * Les permissions du BIEN, distinctes de celles de la procédure.
   *
   * ⚠️ ELLES VIENNENT D'EN HAUT, ELLES NE SE DEVINENT PAS ICI. Un agent peut
   * avoir le droit de clore un dossier sans celui d'activer une parcelle : ce
   * sont deux ressources (`fon.affaire` et `cad.parcelle`) et deux décisions du
   * service. Les confondre ferait afficher un bouton que le serveur refusera —
   * et ce dépôt le dit ailleurs en toutes lettres : « un bouton qui rend 503
   * est pire que pas de bouton ».
   */
  permissionsBien: {
    peutActiver: boolean; peutRattacherFiscal: boolean;
    peutValider: boolean; peutTraiterAnomalie: boolean;
  };
}) {
  const [f, setF] = useState<FicheAffaire | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);
  const [recepisse, setRecepisse] = useState<{ document: string; url: string } | null>(null);

  const charger = useCallback(async () => {
    if (reference === '__nouveau__') return;
    setErreur(null);
    try { setF(await api.affaires.fiche(reference)); }
    catch (e) { setErreur(e instanceof ErreurApi ? e.message : String((e as Error).message ?? e)); }
  }, [reference]);

  useEffect(() => { void charger(); }, [charger]);

  if (reference === '__nouveau__') return <FormulaireOuverture onFerme={onFerme} />;

  const [depot, setDepot] = useState(false);

  /**
   * ⚠️ « AUCUN ACTE » ET « TYPE INCONNU » NE SE REPLIENT PAS L'UN SUR L'AUTRE.
   * Un extrait cadastral RENSEIGNE : il n'écrit rien au registre, et proposer
   * d'y déposer un acte ferait chercher un formulaire qui n'a pas de sens. Un
   * type absent du catalogue partagé, lui, est une erreur de configuration —
   * l'écran le DIT au lieu de proposer un dépôt au hasard.
   */
  const acteImpossible = (() => {
    /* ⚠️ Une vue « existence » ne porte pas de demande : la question du type
       d'acte ne s'y pose pas, et y répondre lirait un champ absent. */
    if (!f || f.visibilite === 'existence' || !f.demande) return null;
    const attendu = acteAttendu(f.demande.type_code);
    if (attendu === null) {
      return `Une « ${f.demande.libelle ?? f.demande.type_code} » n'appelle aucun acte : `
        + `elle renseigne, elle n'inscrit rien au registre foncier.`;
    }
    if (attendu === undefined) {
      return `Le type de demande « ${f.demande.type_code} » n'est pas au catalogue `
        + `des actes attendus (src/partage/acte-attendu.ts) : l'écran ne sait pas quel acte proposer. `
        + `C'est un manque de configuration, pas une erreur de saisie.`;
    }
    return null;
  })();

  async function editerRecepisse() {
    setOccupe(true); setErreur(null); setMessage(null);
    try {
      const r = await api.affaires.editerRecepisse(reference);
      setRecepisse({ document: r.document, url: r.urlTelechargement });
      setMessage(r.message);
      await charger();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : String((e as Error).message ?? e));
    } finally { setOccupe(false); }
  }

  async function clore() {
    const suite = window.prompt(
      `Clore ${reference}. Quelle suite ?\n`
      + `  abouti     — un acte a été validé\n`
      + `  abandonne  — le requérant s'est désisté\n`
      + `  rejete     — la demande n'a pas prospéré\n\n`
      + `Tapez l'un des trois :`);
    if (suite === null) return;
    if (!['abouti', 'abandonne', 'rejete'].includes(suite.trim())) {
      setErreur('Suite inconnue. Attendu : abouti, abandonne ou rejete.'); return;
    }
    const motif = window.prompt('Motif de clôture (10 caractères minimum, consigné au registre) :');
    if (motif === null) return;
    if (motif.trim().length < 10) { setErreur('Une clôture sans motif suffisant est refusée par le registre.'); return; }
    setOccupe(true); setErreur(null);
    try {
      const r = await api.affaires.clore(reference, { suite: suite.trim() as any, motif: motif.trim() });
      setMessage(r.message);
      await charger();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : String((e as Error).message ?? e));
    } finally { setOccupe(false); }
  }

  if (erreur && !f) return (
    <div className="fiche-formulaire large">
      <p className="erreur" role="alert">{erreur}</p>
      <button className="bouton discret" onClick={onFerme}>Retour</button>
    </div>
  );
  if (!f) return <div className="attente-plein">Chargement du dossier {reference}…</div>;

  /**
   * ⚠️⚠️ L'AFFAIRE HORS RESSORT : ON L'AFFICHE, ON NE PLANTE PLUS.
   *
   * Le service rend DEUX formes, et l'écran n'en connaissait qu'une : sur une
   * affaire qui ne relève pas de la commune de l'agent, il lisait
   * `f.requerant.denomination` sur `undefined`, React démontait tout l'arbre,
   * et l'agent voyait une PAGE BLANCHE. Mesuré le 24/09/2026 sur
   * AFF-MTO-2026-00014-1 — une affaire déposée à Matoto pour un fonds de Boké.
   *
   * ⚠️ ET CETTE PAGE BLANCHE ÉTAIT INATTEIGNABLE JUSQU'ICI. Rien ne menait à la
   * fiche d'une affaire hors ressort : c'est la porte posée sur le bureau qui
   * l'a révélée, pas un essai. Le typage la rend désormais impossible —
   * `FicheAffaire` est une union discriminée, et omettre ce cas ne compile plus.
   *
   * ⚠️ ON DIT LA RÈGLE, PAS « ACCÈS REFUSÉ ». L'agent doit comprendre qu'il voit
   * l'existence d'une affaire RÉELLE, dans une autre commune — et non qu'il a
   * buté sur une panne, ou qu'il manque un droit à réclamer.
   */
  if (f.visibilite === 'existence') {
    return (
      <div className="tableau-bord">
        <header className="entete-scene">
          <h2>Affaire <code>{f.affaire.reference}</code></h2>
          <div className="actions">
            <span className={`etat ${COULEUR_ETAPE[f.affaire.etape] ?? 'gris'}`}>{libelleEtape(f.affaire.etape)}</span>
            <button className="bouton discret" onClick={onFerme}>Retour</button>
          </div>
        </header>
        <p className="note-indispo">{f.motif}</p>
        <section>
          <div className="ligne-fiche"><span>Commune compétente</span>
            <strong>{f.commune ?? 'non rattachée'}</strong></div>
          <div className="ligne-fiche"><span>Ouverte le</span>
            <strong>{new Date(f.affaire.ouvert_le).toLocaleDateString('fr-FR')}</strong></div>
          <div className="ligne-fiche"><span>État</span>
            <strong>{f.affaire.vivant ? 'vivante' : 'close'}</strong></div>
        </section>
        {/* ⚠️ NOMMER CE QUI EST MASQUÉ. Un écran qui rendrait simplement moins de
            champs se lirait « cette affaire est vide ». */}
        <section>
          <h4>Ce qui ne vous est pas montré</h4>
          <ul className="liste">
            {f.blocs_masques.map((b) => <li key={b}>{b}</li>)}
          </ul>
        </section>
        {f.impression && !f.impression.possible && (
          <p className="note-indispo">{f.impression.motif}</p>
        )}
      </div>
    );
  }

  return (
    <div className="tableau-bord">
      <header className="entete-scene">
        <h2>Affaire <code>{f.affaire.reference}</code></h2>
        <div className="actions">
          <span className={`etat ${COULEUR_ETAPE[f.affaire.etape] ?? 'gris'}`}>{libelleEtape(f.affaire.etape)}</span>
          <button className="bouton discret" onClick={onFerme}>Retour</button>
        </div>
      </header>

      {f.blocs_masques.length > 0 && (
        <p className="note-indispo">
          Votre habilitation ne couvre pas : {f.blocs_masques.join(', ')}. Ces parties existent
          peut-être — elles ne vous sont pas montrées.
        </p>
      )}
      {message && <p className="succes" role="status">{message}</p>}
      {erreur && <p className="erreur" role="alert">{erreur}</p>}

      {/* ⚠️ LE PARCOURS EN TÊTE. C'est la première question de l'agent : où
          suis-je, et qu'ai-je à faire ? Le placer sous « la demande »
          l'obligerait à défiler pour savoir s'il a quelque chose à faire —
          et un agent qui défile pour trouver son travail ne défile pas. */}
      <ParcoursAffaireVue reference={reference} onChange={() => { void charger(); }} />

      {/* ⚠️ APRÈS LE PARCOURS, AVANT LA DEMANDE — et c'est un ordre, pas un
          rangement. L'agent doit d'abord savoir où en est l'affaire ; il doit
          ensuite pouvoir AGIR sur ce que l'acte imprimera en surbrillance.
          Ranger la complétion en bas de fiche en ferait un écran qu'on
          découvre après avoir imprimé — c'est-à-dire trop tard.

          ⚠️ AUCUNE PERMISSION N'EST PASSÉE D'ICI : la complétion exige
          `fon.affaire:traiter`, et c'est le SERVICE qui le dit dans sa
          réponse. Un écran qui décide lui-même affiche tôt ou tard un
          formulaire ouvert qui rend 403 — mesuré dans ce dépôt : 10 gestes
          sur 24 exigeaient une permission qu'aucun rôle ne détenait, et
          l'écran les affichait quand même. */}
      <FormulaireDeLActe reference={reference} />

      <section>
        <h4>La demande</h4>
        <div className="ligne-fiche"><span>Objet</span><strong>{f.affaire.objet}</strong></div>
        <div className="ligne-fiche">
          <span>Requérant</span>
          <strong>
            {f.requerant.denomination} <span className="discret">({f.requerant.type.replace(/_/g, ' ')})</span>
            {/* ⚠️ ON DIT SI LE RÉQUÉRANT PEUT RECEVOIR DU COURRIER. Sans cette
                mention, l'agent édite un récépissé en croyant qu'il partira. */}
            {!f.requerant.identifie && (
              <b className="pastille refus" title="Aucune identité Mowibo ID : le récépissé ne peut pas être expédié">
                non joignable par courrier
              </b>
            )}
          </strong>
        </div>
        <div className="ligne-fiche">
          <span>Parcelle</span>
          <strong>{f.parcelle
            ? <button className="lien" onClick={() => onOuvrirParcelle(f.parcelle!.identifiant)}>
                <code>{f.parcelle.identifiant}</code> — {f.parcelle.surface_m2} m²
              </button>
            : <span className="discret">pas encore établie — elle naîtra du levé</span>}</strong>
        </div>
        <div className="ligne-fiche">
          <span>Ouvert le</span>
          <strong>{new Date(f.affaire.ouvert_le).toLocaleDateString('fr-FR')}</strong>
        </div>
        {f.affaire.clos_le && (
          <div className="ligne-fiche">
            <span>Clos le</span>
            <strong>{new Date(f.affaire.clos_le).toLocaleDateString('fr-FR')} — {f.affaire.motif_clos}</strong>
          </div>
        )}
      </section>

      <section>
        <h4>Récépissé de dépôt</h4>
        {f.recepisse ? (
          <>
            <div className="ligne-fiche">
              <span>Document</span>
              <strong>
                <code>{f.recepisse.document}</code>{' '}
                <a href={`/documents/${f.recepisse.document}/contenu`} target="_blank" rel="noreferrer">
                  Ouvrir et imprimer
                </a>
              </strong>
            </div>
            <div className="ligne-fiche">
              <span>Expédition</span>
              <strong>
                {f.recepisse.envoi_etat === 'depose'
                  ? <>expédié — envoi <code>{f.recepisse.envoi_reference}</code></>
                  : <span className="discret">
                      non expédié{f.recepisse.echec_motif ? ` — ${f.recepisse.echec_motif}` : ''}
                    </span>}
              </strong>
            </div>
          </>
        ) : (
          <p className="vide">Aucun récépissé édité. Le requérant n'a donc aucune preuve de son dépôt.</p>
        )}
        {recepisse && (
          <p className="succes">
            Récépissé <code>{recepisse.document}</code> édité.{' '}
            <a href={recepisse.url} target="_blank" rel="noreferrer">Ouvrir et imprimer</a>
          </p>
        )}
        <div className="actions">
          <button className="bouton primaire" disabled={occupe || Boolean(f.recepisse)}
                  title={f.recepisse ? 'Déjà édité — un second appel ne rééditerait rien' : "Éditer le récépissé de dépôt"}
                  onClick={() => void editerRecepisse()}>
            {f.recepisse ? 'Récépissé déjà édité' : 'Éditer le récépissé'}
          </button>
        </div>
      </section>

      <section>
        <h4>Le fil du dossier</h4>
        {/* ⚠️ EN AJOUT SEUL : aucune étape ne se modifie ici, et c'est ce qui
            rend le suivi opposable. Les délais se comptent sur ces dates. */}
        <ol className="fil">
          {f.fil.map((e, k) => (
            <li key={k}>
              <span className={`etat ${COULEUR_ETAPE[e.etape] ?? 'gris'}`}>{libelleEtape(e.etape)}</span>
              <span className="fil-date">{new Date(e.survenu_le).toLocaleString('fr-FR')}</span>
              {e.acteur && <span className="discret"> · {e.acteur}</span>}
              {e.commentaire && <div className="fil-note">{e.commentaire}</div>}
            </li>
          ))}
        </ol>
      </section>

      <section>
        <h4>Actes ({f.actes.length})</h4>
        {f.actes.length === 0 ? (
          <>
            <p className="vide">Aucun acte déposé sur ce dossier.</p>
            {/* ⚠️ LE MAILLON QUI MANQUAIT. Le guichet ouvrait la demande et son
                dossier, l'acte se déposait dans un autre écran, et RIEN ne les
                reliait : 13 mutations sur 13 avec `affaire_id` nul, et les 2
                dossiers du registre portant 0 acte. La procédure existait en
                deux moitiés qui ne se joignaient pas. */}
            {peutDeposerActe && !f.affaire.clos_le && (
              depot ? (
                <FormulaireDepot
                  dossier={{
                    reference: f.affaire.reference,
                    parcelle: f.parcelle?.identifiant ?? null,
                    typeSuggere: f.demande ? acteAttendu(f.demande.type_code) ?? null : null,
                  }}
                  onFerme={() => setDepot(false)}
                  onDepose={() => { setDepot(false); void charger(); }} />
              ) : acteImpossible ? (
                <p className="mention">{acteImpossible}</p>
              ) : (
                <div className="actions">
                  <button className="bouton primaire" onClick={() => setDepot(true)}>
                    Déposer l'acte sur ce dossier
                  </button>
                </div>
              )
            )}
          </>
        ) : (
          <table className="tableau">
            <thead><tr><th>Référence</th><th>Nature</th><th>Statut</th><th>Approb.</th></tr></thead>
            <tbody>
              {f.actes.map((m) => (
                <tr key={m.reference}>
                  <td><button className="lien" onClick={() => onOuvrirMutation(m.reference)}><code>{m.reference}</code></button></td>
                  <td>{m.type_libelle}</td>
                  <td>{m.statut}</td>
                  <td>{m.approbations}/2</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/*
        ⚠️⚠️ LA SIGNATURE VIENT APRÈS L'ACTE, ET LA BASE L'IMPOSE.
        `doc.signature.document_id` est NOT NULL : on ne signe pas un
        formulaire, on signe un ACTE. Poser cette section plus haut — au
        guichet, ou avant les actes — inviterait à faire signer une intention.
        Tant qu'aucun acte n'est déposé, le service refuse d'émettre un lien
        et dit pourquoi ; on la montre quand même, pour que l'agent sache
        qu'elle existe et ce qui la débloque.
      */}
      <SignaturesAffaire reference={f.affaire.reference} />

      {f.leves.length > 0 && (
        <section>
          <h4>Levés ({f.leves.length})</h4>
          <table className="tableau">
            <thead><tr><th>Référence</th><th>Méthode</th><th>Précision</th><th>Statut</th></tr></thead>
            <tbody>
              {f.leves.map((l) => (
                <tr key={l.reference}>
                  <td><code>{l.reference}</code></td>
                  <td>{l.methode.replace(/_/g, ' ')}</td>
                  <td>{l.precision_annoncee_m} m</td>
                  <td>{l.statut}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {f.titre && (
        <section>
          <h4>Titre délivré</h4>
          <div className="ligne-fiche">
            <span>Titre foncier</span>
            <strong><code>{f.titre.identifiant}</code> — {f.titre.date_emission}</strong>
          </div>
        </section>
      )}

      <section>
        <h4>Intervenants ({f.acteurs.length})</h4>
        {f.acteurs.length === 0 ? (
          <p className="vide">Aucun intervenant enregistré.</p>
        ) : (
          <ul className="liste-fine">
            {f.acteurs.map((ac, k) => (
              <li key={k}>
                <strong>{ac.role_tenu}</strong> — {ac.organisation ?? 'sans organisation'}
                <span className="discret"> · depuis le {new Date(ac.depuis_le).toLocaleDateString('fr-FR')}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {f.affaire.vivant && (
        <div className="actions">
          <button className="bouton discret danger" disabled={occupe || !peutClore}
                  title={peutClore ? 'Clore ce dossier — la parcelle redeviendra disponible'
                                   : "Votre rôle ne permet pas de clore un dossier (fon.affaire:modifier)"}
                  onClick={() => void clore()}>
            Clore le dossier
          </button>
        </div>
      )}

      {/*
        ⚠️⚠️ L'AFFAIRE ENTIÈRE, SUR LE MÊME ÉCRAN — demande du fondateur,
        14/09/2026 : « quand on ouvre un dossier on doit avoir tous les
        éléments : aperçu, procédures, etc. »

        Jusqu'ici cet écran montrait la PROCÉDURE — demande, récépissé, fil,
        actes, intervenants — et rien du BIEN. Les six onglets de la parcelle
        ne vivaient que dans le tiroir de la carte, qui venait se poser
        par-dessus. Deux surfaces pour une seul'affaire, et l'une cachait
        l'autre.

        ⚠️ C'EST LE MÊME COMPOSANT QUE LE TIROIR, pas une copie : `CorpsParcelle`
        a UN seul exemplaire. Recopier les onglets ici aurait créé deux versions
        qui divergeraient au premier correctif appliqué à une seule.

        ⚠️ ET SANS PARCELLE, ON LE DIT. Un dossier dont le fonds n'est pas
        encore établi — « pas encore établie » dans la liste — n'a pas de bien
        à montrer. Un espace vide se lirait « le chargement a échoué ».
      */}
      <section className="affaire-bien">
        <h3>Le bien</h3>
        {f.parcelle
          ? <CorpsParcelle identifiant={f.parcelle.identifiant} {...permissionsBien} />
          : (
            <p className="vide">
              Ce dossier n'a pas encore de parcelle établie : il n'y a pas de bien à
              présenter tant que le fonds n'est pas identifié.
            </p>
          )}
      </section>
    </div>
  );
}

function FormulaireOuverture({ onFerme }: { onFerme: () => void }) {
  const [requerant, setRequerant] = useState('');
  const [objet, setObjet] = useState('');
  const [parcelle, setParcelle] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [resultat, setResultat] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  async function soumettre(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null); setEnvoi(true);
    try {
      const r = await api.affaires.ouvrir({
        requerantTitulaireId: requerant.trim(),
        objet: objet.trim(),
        identifiantParcelle: parcelle.trim() || undefined,
      });
      setResultat(
        `${r.message}${r.recepisse.expediable ? '' : ` ⚠️ ${r.recepisse.motif}`}`);
    } catch (err) {
      setErreur(err instanceof ErreurApi ? err.message : String(err));
    } finally { setEnvoi(false); }
  }

  return (
    <form className="fiche-formulaire large" onSubmit={soumettre}>
      <h3>Ouvrir un dossier au guichet</h3>

      {/* ⚠️ LE REQUÉRANT EST DÉSIGNÉ, JAMAIS CRÉÉ ICI. Un guichet qui crée une
          personne à chaque visite fabrique des doublons de la même personne, et
          le registre perd le fil de qui possède quoi. */}
      <label>Requérant <span className="aide">identifiant d'un titulaire déjà inscrit au registre</span>
        <input value={requerant} onChange={(e) => setRequerant(e.target.value)} required
               placeholder="UUID du titulaire" />
      </label>

      <label>Objet de la demande <span className="aide">10 caractères minimum, imprimé sur le récépissé</span>
        <textarea value={objet} onChange={(e) => setObjet(e.target.value)} rows={3} required minLength={10} />
      </label>

      <label>Parcelle <span className="aide">facultatif — laissez vide si elle doit encore être levée</span>
        <input value={parcelle} onChange={(e) => setParcelle(e.target.value)}
               placeholder="GN-PAR-… (optionnel)" />
      </label>

      {resultat && <p className="succes" role="status">{resultat}</p>}
      {erreur && <p className="erreur" role="alert">{erreur}</p>}

      <div className="actions">
        <button type="button" className="bouton discret" onClick={onFerme}>Retour</button>
        <button className="bouton primaire"
                disabled={envoi || requerant.trim().length < 10 || objet.trim().length < 10}>
          {envoi ? 'Ouverture…' : 'Ouvrir le dossier'}
        </button>
      </div>
    </form>
  );
}
